/**
 * CalendarPage — calendario eventi (appuntamenti, follow-up, milestone).
 * ★ Fase 59.P2 — split componenti oversize: card evento e dialog estratti in
 * src/components/calendar/ (CalendarEventCard, EventEditDialog, EventNoteDialog,
 * calendarUtils). Comportamento e data-testid INVARIATI.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { toast } from 'sonner';
import { CalendarDays } from 'lucide-react';

import { api } from '../lib/api';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { logger } from '../lib/logger';
import {
  EVENT_TYPE_FILTERS, RANGE_OPTIONS, formatDateGroup,
} from '../components/calendar/calendarUtils';
import { CalendarEventCard } from '../components/calendar/CalendarEventCard';
import { EventEditDialog } from '../components/calendar/EventEditDialog';
import { EventNoteDialog } from '../components/calendar/EventNoteDialog';

export default function CalendarPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [range, setRange] = useState('week');
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const opt = RANGE_OPTIONS.find((o) => o.value === range) || RANGE_OPTIONS[1];
      const r = await api.get(`/calendar/events?days_behind=${opt.behind}&days_ahead=${opt.ahead}`);
      setEvents(r.data || []);
    } catch (e) {
      logger.error('CalendarPage load failed:', e);
      toast.error(i18n.t('calendarX.loadError', 'Errore caricamento calendario'));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // Group events by date
  const grouped = useMemo(() => {
    const filtered = filterType === 'all'
      ? events
      : events.filter((e) => e.type === filterType);
    const map = new Map();
    filtered.forEach((ev) => {
      const dayKey = (ev.event_at || '').slice(0, 10);
      if (!map.has(dayKey)) map.set(dayKey, []);
      map.get(dayKey).push(ev);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [events, filterType]);

  const openEdit = (ev) => {
    setEditEvent(ev);
    setEditForm({
      title: ev.title || '',
      type: ev.type || 'appointment',
      event_at: (ev.event_at || '').slice(0, 16), // for datetime-local
      notes: ev.notes || '',
      status: ev.status || 'pending',
    });
  };

  const closeEdit = () => { setEditEvent(null); setEditForm(null); };

  const saveEdit = async () => {
    if (!editEvent || !editForm) return;
    try {
      await api.patch(`/lead-events/${editEvent.id}`, {
        title: editForm.title,
        type: editForm.type,
        event_at: editForm.event_at ? new Date(editForm.event_at).toISOString() : editEvent.event_at,
        notes: editForm.notes,
        status: editForm.status,
      });
      toast.success('Evento aggiornato');
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || i18n.t('calendarX.updateError', 'Errore aggiornamento'));
    }
  };

  const removeEvent = async (eventId) => {
    if (!window.confirm(i18n.t('calendarX.confirmDeleteEvent', 'Eliminare questo evento?'))) return;
    try {
      await api.delete(`/lead-events/${eventId}`);
      toast.success(i18n.t('calendarX.eventDeleted', 'Evento eliminato'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || i18n.t('common.error', 'Errore'));
    }
  };

  const openNoteDialog = (ev) => {
    setNoteDialog(ev);
    setNoteText('');
  };

  const closeNoteDialog = () => { setNoteDialog(null); setNoteText(''); };

  const appendNote = async () => {
    if (!noteDialog) return;
    const text = noteText.trim();
    if (!text) { toast.error(i18n.t('calendarX.noteTextRequired', 'Inserisci il testo della nota')); return; }
    try {
      await api.post(`/lead-events/${noteDialog.id}/notes`, { text });
      toast.success(i18n.t('calendarX.noteAdded', 'Nota aggiunta'));
      closeNoteDialog();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || i18n.t('common.error', 'Errore'));
    }
  };

  const deleteNote = async (eventId, noteId) => {
    if (!window.confirm(i18n.t('calendarX.confirmDeleteNote', 'Eliminare questa nota?'))) return;
    try {
      await api.delete(`/lead-events/${eventId}/notes/${noteId}`);
      toast.success(i18n.t('calendarX.noteDeleted', 'Nota eliminata'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || i18n.t('common.error', 'Errore'));
    }
  };

  return (
    <div className="p-3 md:p-6 max-w-6xl mx-auto" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-5">
        <div>
          <h1 className="font-serif text-xl md:text-3xl text-[hsl(var(--primary))] flex items-center gap-2">
            <CalendarDays className="w-6 h-6 md:w-7 md:h-7" /> {t('calendar.title', 'Calendario')}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            {t('calendarX.subtitle', 'Appuntamenti, follow-up e milestone di tutti i tuoi lead')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[160px]" data-testid="calendar-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px]" data-testid="calendar-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPE_FILTERS.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-5">
        <Card className="p-3 md:p-4">
          <div className="text-[10px] md:text-xs uppercase tracking-wide text-muted-foreground">Eventi totali</div>
          <div className="text-xl md:text-2xl font-semibold mt-0.5 tabular-nums" data-testid="calendar-stat-total">
            {events.length}
          </div>
        </Card>
        <Card className="p-3 md:p-4">
          <div className="text-[10px] md:text-xs uppercase tracking-wide text-muted-foreground">In programma</div>
          <div className="text-xl md:text-2xl font-semibold mt-0.5 tabular-nums text-amber-700">
            {events.filter((e) => e.status === 'pending').length}
          </div>
        </Card>
        <Card className="p-3 md:p-4">
          <div className="text-[10px] md:text-xs uppercase tracking-wide text-muted-foreground">Completati</div>
          <div className="text-xl md:text-2xl font-semibold mt-0.5 tabular-nums text-emerald-700">
            {events.filter((e) => e.status === 'done').length}
          </div>
        </Card>
      </div>

      {/* Events grouped by day */}
      {loading ? (
        <div className="text-center py-12 text-sm text-muted-foreground">{t('calendarX.loadingEvents', 'Caricamento eventi…')}</div>
      ) : grouped.length === 0 ? (
        <Card className="p-8 text-center" data-testid="calendar-empty">
          <CalendarDays className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
          <div className="text-sm font-medium">{t('calendarX.noEvents', 'Nessun evento')}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {t('calendarX.noEventsHint', 'Non hai eventi nel range selezionato. Crea un appuntamento dalla scheda di un lead.')}
          </div>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="calendar-event-list">
          {grouped.map(([day, dayEvents]) => (
            <div key={day}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-2 capitalize">
                {formatDateGroup(`${day}T00:00:00Z`)}
              </div>
              <div className="space-y-2">
                {dayEvents.map((ev) => (
                  <CalendarEventCard
                    key={ev.id}
                    ev={ev}
                    onAddNote={openNoteDialog}
                    onEdit={openEdit}
                    onDelete={removeEvent}
                    onDeleteNote={deleteNote}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <EventEditDialog
        editEvent={editEvent}
        editForm={editForm}
        setEditForm={setEditForm}
        onClose={closeEdit}
        onSave={saveEdit}
      />

      {/* Add note dialog */}
      <EventNoteDialog
        noteDialog={noteDialog}
        noteText={noteText}
        setNoteText={setNoteText}
        onClose={closeNoteDialog}
        onSave={appendNote}
      />
    </div>
  );
}
